// app/VAi.js
import React, { useState } from 'react';
import {
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator
} from 'react-native';
import axios from 'axios';
import API_BASE_URL from '../apiConfig.js';

// Helper to fetch a token from localStorage
const getToken = () => {
  try {
    return localStorage.getItem('userToken');
  } catch {
    return null;
  }
};

// Tiny helper to cap long strings for token efficiency
const trimText = (s = '', n = 220) => (s.length > n ? `${s.slice(0, n)}…` : s);

export default function VAi() {
  const [input, setInput] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);

  const handleQuery = async () => {
    const q = input.trim();
    if (!q) return;

    setLoading(true);
    setResponse('');

    try {
      const token = getToken();
      const auth = token ? { Authorization: `Bearer ${token}` } : {};

      // Fetch user data (auth-protected)
      const [customersRes, jobsRes, invoicesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/customers`, { headers: auth }),
        axios.get(`${API_BASE_URL}/api/jobs`, { headers: auth }),
        axios.get(`${API_BASE_URL}/api/invoices`, { headers: auth })
      ]);

      // Trim and shape data before sending to your VAi proxy
      const customers = (customersRes.data || []).slice(0, 50).map(c => ({
        id: c.id,
        name: trimText(c.name, 80),
        city: c.city,
        state: c.state,
        zip: c.zip,
        phone: c.phone,
        email: c.email
      }));

      const jobs = (jobsRes.data || []).slice(0, 75).map(j => ({
        id: j.id,
        customerName: trimText(j.customerName, 80),
        title: trimText(j.title, 120),
        description: trimText(j.description, 220),
        price: j.price,
        selectedDate: j.selectedDate || j.date,
        selectedTime: j.selectedTime || j.time,
        city: j.city,
        state: j.state,
        zip: j.zip,
        completed: j.completed,
        visitType: j.visitType // "job" | "quote"
      }));

      const invoices = (invoicesRes.data || []).slice(0, 75).map(inv => ({
        id: inv.id,
        customer: inv.customer || inv.customerName,
        title: trimText(inv.title, 120),
        description: trimText(inv.description, 200),
        price: inv.price,
        selectedDate: inv.selectedDate,
        selectedTime: inv.selectedTime,
        sentEmail: inv.sentEmail,
        sentText: inv.sentText
      }));

      // Compose prompt for your proxy endpoint
      const prompt = `
User asked: "${q}"

Context (JSON, trimmed):
${JSON.stringify(
  {
    meta: {
      customers_count: customers.length,
      jobs_count: jobs.length,
      invoices_count: invoices.length
    },
    customers,
    jobs,
    invoices
  },
  null,
  0
)}
Please answer succinctly and show calculations when relevant. If data is insufficient, say what else is needed.
      `.trim();

      // Call your VAi proxy (server-side)
      const res = await axios.post(
        'https://tradebookcrm.com/query',
        { prompt },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const answer = res?.data?.response || res?.data?.answer || 'No response.';
      setResponse(String(answer).trim());
      setInput(''); // Clear input on success
    } catch (error) {
      console.error('VAi error:', error);
      const msg =
        error?.response?.data?.error ||
        error?.message ||
        'Something went wrong. Please try again.';
      setResponse(String(msg));
    } finally {
      setLoading(false);
    }
  };

  const disabled = loading || !input.trim();

  return (
    <ScrollView contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
      <Text style={styles.title}>Ask VAi</Text>
      <TextInput
        style={styles.input}
        placeholder='Ask a question or give a command (e.g., "Total unpaid invoices", "Jobs this week", "Quotes for Smith")'
        value={input}
        onChangeText={setInput}
        multiline
      />
      <TouchableOpacity
        style={[styles.button, disabled && { opacity: 0.6 }]}
        onPress={handleQuery}
        disabled={disabled}
      >
        <Text style={styles.buttonText}>{loading ? 'Working…' : 'Submit'}</Text>
      </TouchableOpacity>
      {loading ? (
        <ActivityIndicator size="large" color="#007AFF" />
      ) : (
        !!response && <Text style={styles.response}>{response}</Text>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { padding: 20, backgroundColor: '#fff', flexGrow: 1 },
  title: { fontSize: 22, fontWeight: 'bold', marginBottom: 20, textAlign: 'center' },
  input: {
    borderWidth: 1, borderColor: '#ccc', padding: 12, borderRadius: 6,
    marginBottom: 20, minHeight: 100, textAlignVertical: 'top'
  },
  button: { backgroundColor: '#007AFF', padding: 12, borderRadius: 6, alignItems: 'center', marginBottom: 20 },
  buttonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },
  response: { fontSize: 16, color: '#333' }
});
